import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconX } from '@tabler/icons-react';
import { LoadingButton } from '@mui/lab';
import { FormInput } from '@components/FormInput/ui/FormInput';
import useToast from '@hooks/useToast';
import {
  UseCaseGroupFormFields,
  useCaseGroupValidationSchema,
} from '@features/admin-use-case-group/utils/form-validators';
import {
  createUseCaseGroup,
  updateUseCaseGroup,
  UseCaseGroup,
} from '@api/use-case-group';
import { BackendError, extractBackendError } from '@utils/handle-backend-error';

interface UseCaseGroupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  group?: UseCaseGroup | null;
}

function UseCaseGroupModal({
  open,
  onClose,
  onSuccess,
  group,
}: UseCaseGroupModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { showError, showSuccess } = useToast();

  const methods = useForm<UseCaseGroupFormFields>({
    resolver: yupResolver(useCaseGroupValidationSchema),
    defaultValues: {
      name: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = methods;

  useEffect(() => {
    if (group) {
      setValue('name', group.name);
    } else {
      reset({ name: '' });
    }
  }, [group, setValue, reset]);

  const onSubmit = async (data: UseCaseGroupFormFields) => {
    setIsLoading(true);
    try {
      if (group) {
        await updateUseCaseGroup(group.id, data);
        showSuccess('Success', 'Use Case Group updated successfully');
      } else {
        await createUseCaseGroup(data);
        showSuccess('Success', 'Use Case Group created successfully');
      }
      reset();
      onSuccess();
      onClose();
    } catch (error) {
      const backendError = extractBackendError(error as BackendError);
      showError('Error', backendError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h3">
                {group ? 'Edit Use Case Group' : 'Create Use Case Group'}
              </Typography>
            </Box>
            <IconButton onClick={handleClose}>
              <IconX />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12}>
                <FormInput
                  type="input"
                  required
                  labelText="Name"
                  placeholder="Enter use case group name"
                  errorText={
                    errors?.name?.message ? errors?.name?.message : errors?.name
                  }
                  {...register('name')}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Box
            paddingX={3}
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Button variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              loading={isLoading}
            >
              {group ? 'Update' : 'Create'}
            </LoadingButton>
          </Box>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default UseCaseGroupModal;
